<template>
  <BuilderMatchMedia v-slot="{ matches: isDesktop }" device="desktop">
    <div
      tabindex="0"
      :class="{ 'card-product': true, selected: isSelected }"
      data-testid="card-product"
      role="button"
      @click="onSelect"
      @keydown.enter.space="onSelect"
    >
      <Row data-testid="row-title" :has-line="false">
        <template #top-left>
          <div data-testid="risk-score">
            <TagRiskLevel :risk-grade="instrument.risk.value" />
          </div>
        </template>
        <template #bottom-left>
          <div data-testid="title" class="font-size-title">
            {{ instrument.name }}
          </div>
        </template>
      </Row>
      <hr class="card-products-divider" />
      <Grid :columns="[2, 3, 3, 2]">
        <Row data-testid="row-benchmarkFormatted" :has-line="false">
          <template #top-left>
            <div class="font-size label">Rentabilidade</div>
          </template>
          <template v-if="!isDesktop" #top-right>
            <div class="font-size value">
              {{ benchmarkFormatted }}
            </div>
          </template>
          <template v-else #bottom-left>
            <div class="font-size value">
              {{ benchmarkFormatted }}
            </div>
          </template>
        </Row>
        <Row
          :class="{ 'row-center': isDesktop }"
          data-testid="row-minimumInitialInvestment"
          :has-line="false"
        >
          <template #top-left>
            <div class="font-size label">Depósito inicial</div>
          </template>
          <template v-if="!isDesktop" #top-right>
            <div class="font-size value">
              {{ instrument.minimumInitialInvestment.formatted }}
            </div>
          </template>
          <template v-else #bottom-left>
            <div class="font-size value">
              {{ instrument.minimumInitialInvestment.formatted }}
            </div>
          </template>
        </Row>
        <Row
          :class="{ 'row-center': isDesktop }"
          data-testid="row-minimumFinancialMovement"
          :has-line="false"
        >
          <template #top-left>
            <div class="font-size label">Mov. mínima</div>
          </template>
          <template v-if="!isDesktop" #top-right>
            <div class="font-size value">
              {{ instrument.minimumFinancialMovement.formatted }}
            </div>
          </template>
          <template v-else #bottom-left>
            <div class="font-size value">
              {{ instrument.minimumFinancialMovement.formatted }}
            </div>
          </template>
        </Row>
        <Row
          :class="{ 'row-end': isDesktop }"
          data-testid="row-liquidity"
          :has-line="false"
        >
          <template #top-left>
            <div class="font-size label">{{ liquidityFormatted.label }}</div>
          </template>
          <template v-if="!isDesktop" #top-right>
            <div class="font-size label">
              {{ liquidityFormatted.value }}
            </div>
          </template>
          <template v-else #bottom-left>
            <div class="font-size value">
              {{ liquidityFormatted.value }}
            </div>
          </template>
        </Row>
      </Grid>
    </div>
  </BuilderMatchMedia>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Grid, Row } from '@warrenbrasil/nebraska-web';

import { BuilderMatchMedia } from '../BuilderMatchMedia';

import TagRiskLevel from '../TagRiskLevel/TagRiskLevel.vue';
import { Product } from '../../types';
import { IProductRemove } from '../../services/types/models/IBffInvestments';

interface Props {
  instrument: Product;
  addProduct?: boolean;
  isSelected: boolean;
}

interface EmitEvents {
  (event: 'handle-instrument-click', { id }: IProductRemove): void;
}

const props = defineProps<Props>();
const emit = defineEmits<EmitEvents>();

const liquidityFormatted = computed(() => {
  return {
    label: props.instrument.withdrawPeriod.label,
    value: props.instrument.withdrawPeriod.value
  };
});

const benchmarkFormatted = computed(() => {
  return props.instrument.rentability ?? '-';
});

function onSelect() {
  emit('handle-instrument-click', {
    id: props.instrument.id
  });
}
</script>

<style lang="less" scoped>
/deep/ .row-item > div {
  padding: 0 !important;
  margin: 0 !important;
}

/deep/ .row-item > div .starts {
  margin-bottom: @size-spacing-x150 !important;
}

/deep/ .row-item {
  background-color: transparent !important;
}

.card-product {
  background: @background-secondary;
  width: 100%;
  padding: @size-spacing-x400;
  cursor: pointer;
  border: solid @size-border-x400 @divider-primary;
  border-radius: @size-radius-x200;
  margin-bottom: @size-spacing-x300;

  &.selected {
    background: @background-tertiary;
  }
}

.card-product:hover {
  background: @background-primary;

  &.selected {
    background: @background-tertiary;
  }
}

.card-products-divider {
  border-top: solid @size-border-x400 @divider-primary!important;
}

.font-size-title {
  font-size: @size-spacing-x450 !important;
}

.font-size {
  font-size: @size-spacing-x400 !important;
}

.value {
  color: @text-primary;
}

.label {
  color: @text-secondary;
}

.row-end {
  justify-self: flex-end;
}

.row-center {
  justify-self: center;
}

.row-margin {
  margin-right: @size-spacing-x450;
}
</style>
